import { REMOTE_RECORD_DEBUG, LOG_BACKEND_URL, LOG_BROWSER_FILTER, LOG_MINIMUM_LEVEL, LOG_TYPES, LOG_TYPE_FILTER } from '../constants/constants';
import { getBrowserName } from './deviceDetection';

const isDevelopment = process.env.NODE_ENV === 'development';

// Log level hierarchy
const LOG_LEVELS = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Check if log type should be filtered
const shouldLogType = (type: string | null | undefined): boolean => {
  if (!type || LOG_TYPE_FILTER === null) {
    return true; // No type specified or no filter applied
  }
  return LOG_TYPE_FILTER.includes(type as keyof typeof LOG_TYPES);
};

// Check if log level meets minimum
const shouldLog = (level: string): boolean => {
  const currentLevel = LOG_LEVELS[level.toLowerCase() as keyof typeof LOG_LEVELS] ?? 0;
  const minimumLevel = LOG_LEVELS[LOG_MINIMUM_LEVEL.toLowerCase() as keyof typeof LOG_LEVELS] ?? 1;
  return currentLevel >= minimumLevel;
};

// Cache for user IP address (fetched once at app startup)
let cachedIP: string | null | undefined;
let ipFetchPromise: Promise<string | null | undefined> | null = null;

// Get user IP address with timeout (cached after first fetch)
const getUserIP = async (): Promise<string | null | undefined> => {
  // Return cached IP if already fetched
  if (cachedIP !== undefined) {
    return cachedIP;
  }

  // If fetch is already in progress, wait for it instead of starting another
  if (ipFetchPromise) {
    return ipFetchPromise;
  }

  // Start the fetch and store the promise (non-blocking with reasonable timeout for mobile)
  ipFetchPromise = (async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 3000); // Increased timeout for reliability

      const response = await fetch('https://api.ipify.org?format=json', {
        signal: controller.signal,
      });
      clearTimeout(timeout);
      
      const data = await response.json();
      cachedIP = data.ip;
      return cachedIP;
    } catch {
      cachedIP = null; // Cache the null result to avoid retrying
      return null;
    } finally {
      ipFetchPromise = null; // Clear the promise after completion
    }
  })();

  return ipFetchPromise;
};

// Get user name from localStorage
const getUserName = (): string | null => {
  try {
    return localStorage.getItem('username');
  } catch (error) {
    console.debug('[Logger] Failed to get username from localStorage:', error);
    return null;
  }
};

// Send log to backend with timeout (non-blocking during startup)
const sendLogToBackend = async (level: string, message: string, type: string | null = null) => {
   try {
     if (!shouldLog(level) || !shouldLogType(type)) {
       return;
     }

     const browser = getBrowserName();
     if (!LOG_BROWSER_FILTER.includes(browser)) {
       return;
     }

     // Always defer backend logging to avoid blocking main thread message handlers
     // Fire and forget - don't await
     sendLogToBackendDeferred(level, message, type).catch(error => {
       console.debug('[LOGGER] Backend logging error:', (error as any)?.message || error);
     });
   } catch (error) {
     console.log('[LOGGER] Error:', (error as any)?.message || error);
   }
};

// Deferred backend logging function
const sendLogToBackendDeferred = async (level: string, message: string, type: string | null = null) => {
  try {
    if (!shouldLog(level) || !shouldLogType(type)) {
      return;
    }

    const browser = getBrowserName();
    if (!LOG_BROWSER_FILTER.includes(browser)) {
      return;
    }

    // Collect user info (with timeout for IP)
    const [ipAddress, userName] = await Promise.all([
      getUserIP(),
      Promise.resolve(getUserName()),
    ]);

    const timestamp = new Date().toLocaleString('sv-SE');
    
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000); // Increased timeout for reliability
    
    await fetch(LOG_BACKEND_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      signal: controller.signal,
      body: JSON.stringify({
        message,
        level,
        type,
        browser,
        userAgent: navigator.userAgent,
        ipAddress,
        userName,
        timestamp,
      }),
    });
    
    clearTimeout(timeout);
  } catch (error) {
    console.log('[LOGGER] Error:', (error as any)?.message || error);
  }
};

// Helper to extract log type from arguments
const extractLogType = (args: any[]): { type: string | null; cleanArgs: any[] } => {
  if (args.length === 0) return { type: null, cleanArgs: args };
  
  const firstArg = args[0];
  if (typeof firstArg === 'string') {
    // Check if first argument contains a log type in brackets [TYPE]
    const typeMatch = firstArg.match(/^\[([A-Z_]+)\]/);
    if (typeMatch) {
      const type = typeMatch[1];
      // Remove the type prefix from the message
      const cleanMessage = firstArg.replace(/^\[[A-Z_]+\]\s*/, '');
      return { type, cleanArgs: [cleanMessage, ...args.slice(1)] };
    }
  }
  
  return { type: null, cleanArgs: args };
};

// Helper to stringify args properly
const stringifyArgs = (args: any[]): string => {
  return args.map(arg => {
    if (typeof arg === 'object' && arg !== null) {
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    }
    return String(arg);
  }).join(' ');
};

const logger = {
  log: (...args: any[]) => {
    const { type, cleanArgs } = extractLogType(args);
    if (isDevelopment) {
      // Defer console.log to avoid blocking main thread when logging large objects
      setTimeout(() => console.log(...cleanArgs), 0);
    }
    if (REMOTE_RECORD_DEBUG) {
      sendLogToBackend('debug', stringifyArgs(cleanArgs), type);
    }
  },
  warn: (...args: any[]) => {
    const { type, cleanArgs } = extractLogType(args);
    if (isDevelopment) {
      setTimeout(() => console.warn(...cleanArgs), 0);
    }
    if (REMOTE_RECORD_DEBUG) {
      sendLogToBackend('warn', stringifyArgs(cleanArgs), type);
    }
  },
  error: (...args: any[]) => {
    const { type, cleanArgs } = extractLogType(args);
    if (isDevelopment) {
      setTimeout(() => console.error(...cleanArgs), 0);
    }
    if (REMOTE_RECORD_DEBUG) {
      sendLogToBackend('error', stringifyArgs(cleanArgs), type);
    }
  },
  info: (...args: any[]) => {
    const { type, cleanArgs } = extractLogType(args);
    if (isDevelopment) {
      setTimeout(() => console.info(...cleanArgs), 0);
    }
    if (REMOTE_RECORD_DEBUG) {
      sendLogToBackend('info', stringifyArgs(cleanArgs), type);
    }
  },
  debug: (...args: any[]) => {
    const { type, cleanArgs } = extractLogType(args);
    if (isDevelopment) {
      setTimeout(() => console.debug(...cleanArgs), 0);
    }
    if (REMOTE_RECORD_DEBUG) {
      sendLogToBackend('debug', stringifyArgs(cleanArgs), type);
    }
  },
};

export default logger;
